import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import UserNameImg from '../../assets/icons/silhouette.svg';
import twoFAPic from '../../assets/images/person-with-login-screen.svg';
import { useAppSelector } from '../../hooks';
import useForm from '../../utils/customHooks/useForm';
import { secondsToTimeFormat } from '../../utils/date';
import { selectUser } from '../Login/LoginSlice';
import { login } from '../Login/LoginSlice/actionCreators';
import {
  select2faError,
  selectEmail,
  selectLegacy2faData,
  selectPassword,
  setEmail,
  setIsError,
  setPassword,
} from './Login2faSlice';
import { login2FACode } from './Login2faSlice/actionCreators';
import {
  SButton,
  SErrorText,
  SForm,
  SImg,
  SInput,
  SLInk,
  SLinkText,
  SPage,
  SParagraph,
  SSection,
  SSmall,
  STimer,
} from './styles';

const SEmptyBox = styled.div`
  height: 40px;
`;
export const Login2fa = () => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const { t } = useTranslation();

  const login2faError = useAppSelector(select2faError);
  const legacy2faData = useAppSelector(selectLegacy2faData);
  const user = useAppSelector(selectUser);
  const defaultEmail = useAppSelector(selectEmail);
  const defaultPassword = useAppSelector(selectPassword);
  const [timerLoading, setTimerLoading] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(59);
  const { inputs, handleChange } = useForm({
    initial: {
      code: '',
      email: defaultEmail,
      password: defaultPassword,
    },
    validateSchema: {},
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputs.code.length === 0) {
      dispatch(setIsError(true));
    } else {
      dispatch(
        login2FACode({
          code: inputs.code,
          email: inputs.email,
          password: inputs.password,
        })
      );
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (timerLoading) setSeconds((sec: number) => sec - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timerLoading]);
  const onResend = () => {
    setTimerLoading(true);
    dispatch(login({ email: inputs.email, password: inputs.password }));
    setTimeout(() => {
      setTimerLoading(false);
      setSeconds(60);
    }, 60000);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange(e as React.ChangeEvent<HTMLInputElement>);

  useEffect(() => {
    if (legacy2faData) {
      return;
    }

    dispatch(setIsError(false));
    navigation('/login', { replace: true });
    dispatch(setEmail(''));
    dispatch(setPassword(''));
  }, [legacy2faData]);

  useEffect(() => {
    if (!user) {
      return;
    }

    dispatch(setIsError(false));
    navigation('/dashboard', { replace: true });
    dispatch(setEmail(''));
    dispatch(setPassword(''));
  }, [user]);

  return (
    <SPage>
      <SImg>
        <img src={twoFAPic} alt="2faPic" />
      </SImg>

      <SSection>
        <SParagraph>{t('two_fa_title')}</SParagraph>
        <SForm onSubmit={handleSubmit}>
          <SInput
            isShadow
            error={login2faError}
            name="code"
            placeHolderTx={t('two_fa_input_placeholder')}
            value={inputs.code}
            onChange={onChange}
            StartAdornment={<img src={UserNameImg} alt="username icon" />}
          />
          <>
            {login2faError ? <SErrorText>{t('two_fa_input_error')}</SErrorText> : <div />}
            <SEmptyBox />
          </>
          <SSmall>{t(`two_fa_info_text`, { phone: legacy2faData?.TwoFactorAuthPhone })}</SSmall>
          <SButton text={t('two_fa_submit')} />
        </SForm>
        {timerLoading ? (
          <SLinkText>
            {t('resend_timer_text')}
            <STimer>{secondsToTimeFormat(seconds)}</STimer>
          </SLinkText>
        ) : (
          <SLinkText>
            {t('two_fa_resend_text')}
            <SLInk onClick={onResend}> {t('two_fa_resend_button')}</SLInk>
          </SLinkText>
        )}
      </SSection>
    </SPage>
  );
};
